import React, { useEffect, useState } from "react";
import { Monitor, Smartphone, MailOpen, MousePointerClick, Send, Loader2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";

export function DeviceToggle({ device, onChange }) {
  return (
    <div className="inline-flex border border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="ec-device-toggle">
      {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Phone"]].map(([k, I, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 ${device === k ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D] hover:text-[#0B132B]"}`} data-testid={`ec-device-${k}`}><I size={12} /> {l}</button>
      ))}
    </div>
  );
}

export function EmailFrame({ html, device, height = 720 }) {
  const mobile = device === "mobile";
  return (
    <div className="bg-[#E9ECEF] rounded-sm p-4 flex justify-center" data-testid="ec-preview-frame">
      <div className={`bg-white transition-[width] duration-300 ${mobile ? "w-[375px] rounded-[28px] border-[10px] border-[#0B132B] shadow-xl overflow-hidden" : "w-full"}`} data-testid={`ec-frame-${device}`}>
        {mobile && <div className="h-5 bg-[#0B132B] flex justify-center"><div className="w-20 h-3 bg-[#1f2a48] rounded-b-xl" /></div>}
        <iframe title="preview" srcDoc={html} style={{ height }} className="w-full bg-white block" sandbox="" />
      </div>
    </div>
  );
}

const Stat = ({ icon: I, label, value, sub, testid }) => (
  <div className="border border-[#E9ECEF] rounded-sm p-3 bg-white" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.16em] font-bold text-[#6C757D]"><I size={10} className="text-[#D4AF37]" /> {label}</div>
    <div className="text-2xl font-black text-[#0B132B]">{value}</div>
    {sub && <div className="text-[10px] text-[#6C757D]">{sub}</div>}
  </div>
);

export function CampaignDetailDialog({ campaignId, onClose, onReuse }) {
  const [d, setD] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [tab, setTab] = useState("email");
  useEffect(() => { if (!campaignId) return; setD(null); api.get(`/email-campaigns/history/${campaignId}`).then(({ data }) => setD(data)).catch(() => { toast.error("Could not load campaign"); onClose(); }); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  return (
    <Dialog open={!!campaignId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-4xl p-0 overflow-hidden" data-testid="ec-campaign-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E9ECEF]">
          <DialogTitle className="truncate pr-6">{d?.subject || "Campaign"}</DialogTitle>
          {d && <div className="text-[11px] text-[#6C757D]">{d.user_email} · {d.sent_at ? new Date(d.sent_at).toLocaleString() : d.send_at ? `scheduled ${new Date(d.send_at).toLocaleString()}` : ""} · <span className="uppercase font-bold">{d.status}</span>{d.test_only && " · test"}</div>}
        </DialogHeader>
        {!d ? <div className="p-8 text-sm text-[#6C757D] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div> : (
          <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat icon={Send} label="Delivered" value={d.sent ?? d.recipients} sub={d.failed?.length ? `${d.failed.length} failed` : `${d.recipients} recipients`} testid="ec-cd-sent" />
              <Stat icon={MailOpen} label="Open rate" value={pct(d.open_rate)} sub={`${d.opens} of ${d.tracked} · ${d.total_opens} total opens`} testid="ec-cd-opens" />
              <Stat icon={MousePointerClick} label="Click rate" value={pct(d.click_rate)} sub={`${d.clicks} clicked · ${d.total_clicks} total`} testid="ec-cd-clicks" />
              <Stat icon={MousePointerClick} label="Click-to-open" value={d.opens ? `${Math.round((d.clicks / d.opens) * 100)}%` : "—"} sub="clicks ÷ opens" testid="ec-cd-ctor" />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex gap-1">{[["email", "What was sent"], ["people", `Recipients (${d.recipients_detail.length})`]].map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-sm text-xs font-bold ${tab === k ? "bg-[#0B132B] text-white" : "text-[#6C757D] hover:bg-[#F8F9FA]"}`} data-testid={`ec-cd-tab-${k}`}>{l}</button>)}</div>
              <div className="flex items-center gap-2">
                {tab === "email" && <DeviceToggle device={device} onChange={setDevice} />}
                {d.html && onReuse && <Button size="sm" variant="outline" onClick={() => onReuse(d)} className="h-8 rounded-sm" data-testid="ec-cd-reuse"><Copy size={12} className="mr-1" /> Reuse as new campaign</Button>}
              </div>
            </div>
            {tab === "email" ? (d.html ? <EmailFrame html={d.html} device={device} height={560} /> : <div className="text-sm italic text-[#6C757D]">Content wasn't stored for this older campaign.</div>) : (
              d.recipients_detail.length === 0 ? <div className="text-sm italic text-[#6C757D]">No tracked messages for this campaign.</div> : (
                <div className="divide-y divide-[#F1F3F5] text-sm border border-[#E9ECEF] rounded-sm" data-testid="ec-cd-recipients">
                  {d.recipients_detail.map((m) => (
                    <div key={m.email} className="flex items-center justify-between px-3 py-2">
                      <div><div className="font-mono text-xs">{m.email}</div>{m.last_click_url && <div className="text-[10px] text-[#6C757D] truncate max-w-[420px]">↳ {m.last_click_url}</div>}</div>
                      <div className="text-xs whitespace-nowrap">{m.opened ? <span className="text-green-700 font-semibold">opened{m.opened > 1 ? ` ×${m.opened}` : ""}</span> : <span className="text-[#6C757D]">not opened</span>}{m.clicked ? <span className="ml-2 text-[#0B132B] font-semibold">clicked{m.clicked > 1 ? ` ×${m.clicked}` : ""}</span> : null}{m.last_event_at && <span className="ml-2 text-[10px] text-[#6C757D]">{new Date(m.last_event_at).toLocaleString()}</span>}</div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
